import { useState } from "react";
import axios from "axios";
import { X, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
  "w-full bg-transparent border border-[#26241d] px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase text-[#f5f2ea] placeholder:text-[#57534a] focus:outline-none focus:border-[#C9A227] transition-colors";

export default function WaitlistModal({ open, onClose }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        full_name: fullName,
        phone: phone || null,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="waitlist-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Join the W8LIST"
    >
      <div
        className="relative w-full max-w-md bg-[#0d0d0c] border border-[#1f1e1a] p-8 sm:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          data-testid="waitlist-close-button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#57534a] hover:text-[#f5f2ea] transition-colors"
        >
          <X size={16} />
        </button>

        {success ? (
          <div data-testid="waitlist-success" className="py-10 text-center">
            <div className="mx-auto mb-6 flex h-10 w-10 items-center justify-center border border-[#C9A227] text-[#C9A227]">
              <Check size={18} />
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#f5f2ea]">
              YOU W8ED LONG ENOUGH.
            </h2>
            <p className="mt-3 font-mono text-[11px] tracking-[0.3em] uppercase text-[#C9A227]">
              Welcome to W8ED.
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-mono text-xs tracking-[0.4em] uppercase text-[#C9A227]">
              The W8LIST
            </h2>
            <p className="mt-3 mb-8 font-mono text-[10px] tracking-[0.15em] uppercase text-[#57534a]">
              Be first to know what we're building.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                required
                data-testid="waitlist-fullname-input"
                placeholder="Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={inputClass}
              />
              <input
                type="email"
                required
                data-testid="waitlist-email-input"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
              <input
                type="tel"
                data-testid="waitlist-phone-input"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />

              {error && (
                <p
                  data-testid="waitlist-error"
                  className="font-mono text-[10px] tracking-[0.15em] uppercase text-red-500"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="waitlist-submit-button"
                disabled={submitting}
                className="w-full bg-[#f5f2ea] text-[#0a0a09] font-mono text-[11px] tracking-[0.3em] uppercase py-3.5 hover:bg-[#C9A227] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Joining..." : "JOIN THE W8LIST"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
